import React from "react";
import { cn } from "../../lib/utils.js";
import {
  CHOICE_CARD_LABEL,
  CHOICE_CARD_TRAILING,
  CHOICE_DETAIL,
  CHOICE_DIMMED,
  CHOICE_ICON,
  CHOICE_ROW,
  CHOICE_TEXT,
  CHOICE_TRAILING,
  choiceCardRow,
} from "../../lib/choice-card.js";
import { tintClasses } from "../../lib/tints.js";
import type { RadioGroupVariant, RadioOption } from "./RadioGroup.js";

/* One option of a RadioGroup. Internal: not in the barrel. With no option
   field set and the default variant it renders today's lone <label>. */

interface RadioOptionRowProps {
  option: RadioOption;
  /** Unique per option; prefixes the label, description and reason ids. */
  id: string;
  name: string;
  checked: boolean;
  variant: RadioGroupVariant;
  onSelect: () => void;
}

export function RadioOptionRow({
  option: o,
  id,
  name,
  checked,
  variant,
  onSelect,
}: RadioOptionRowProps) {
  const disabled = o.disabledReason !== undefined;
  // A swatch is a 24px tinted circle, not a text row: the label is sr-only
  // (it is still the radio's accessible name) and colour comes only from
  // `tint` through lib/tints.ts — never a className or style hook.
  if (variant === "swatch")
    return (
      <label
        className={cn(
          "inline-flex cursor-pointer",
          disabled && ["cursor-not-allowed", CHOICE_DIMMED],
        )}
      >
        <input
          type="radio"
          name={name}
          value={o.value}
          checked={checked}
          disabled={disabled}
          aria-describedby={disabled ? `${id}-reason` : undefined}
          onChange={onSelect}
          className="peer sr-only"
        />
        <span
          aria-hidden="true"
          className={cn(
            "h-6 w-6 rounded-full",
            o.tint !== undefined && tintClasses(o.tint).dot,
            checked && "ring-2 ring-pb-teal",
            "peer-focus-visible:ring-2 peer-focus-visible:ring-pb-teal",
          )}
        />
        <span className="sr-only">{o.label}</span>
      </label>
    );
  const card = variant === "card";
  const dim = disabled && CHOICE_DIMMED;
  // A description or reason sits INSIDE the label so the whole row picks the
  // option, so the radio takes its name from the label text alone and hears
  // the rest as its description.
  const detail = Boolean(o.description) || disabled;
  const describedBy =
    [
      o.description ? `${id}-description` : null,
      disabled ? `${id}-reason` : null,
    ]
      .filter((part): part is string => part !== null)
      .join(" ") || undefined;
  const optionName = (
    <span
      id={detail ? `${id}-label` : undefined}
      className={cn("text-sm text-pb-text", dim)}
    >
      {o.label}
    </span>
  );
  const label = (
    <label
      className={cn(
        card
          ? [CHOICE_CARD_LABEL, "cursor-pointer"]
          : "inline-flex items-center gap-2 cursor-pointer select-none",
        disabled && "cursor-not-allowed",
      )}
    >
      <span className={cn("relative inline-flex", dim)}>
        <input
          type="radio"
          name={name}
          value={o.value}
          checked={checked}
          disabled={disabled}
          aria-labelledby={detail ? `${id}-label` : undefined}
          aria-describedby={describedBy}
          onChange={onSelect}
          className="peer sr-only"
        />
        <span
          className={cn(
            "w-4 h-4 rounded-full border flex items-center justify-center transition-colors",
            checked
              ? "border-pb-teal bg-pb-card"
              : "border-pb-control-border bg-pb-card",
            "peer-focus-visible:ring-2 peer-focus-visible:ring-pb-teal/40",
          )}
        >
          {/* ADR-0091: the dot is ALWAYS rendered so it has something
              to animate from — the ring fades its colour, and a dot
              that appears in one frame reads as a flicker against it. */}
          <span
            aria-hidden="true"
            data-checked={checked ? "true" : "false"}
            className="pb-control-mark w-2 h-2 rounded-full bg-pb-teal"
          />
        </span>
      </span>
      {o.icon && (
        <span aria-hidden="true" className={cn(CHOICE_ICON, dim)}>
          {o.icon}
        </span>
      )}
      {detail ? (
        <span className={CHOICE_TEXT}>
          {optionName}
          {o.description && (
            <span id={`${id}-description`} className={cn(CHOICE_DETAIL, dim)}>
              {o.description}
            </span>
          )}
          {/* The reason stays at full strength: it is the one line that
              explains the dimmed option. */}
          {disabled && (
            <span id={`${id}-reason`} className={CHOICE_DETAIL}>
              {o.disabledReason}
            </span>
          )}
        </span>
      ) : (
        optionName
      )}
    </label>
  );
  // The trailing slot sits beside the label, never inside it.
  if (card)
    return (
      <div className={choiceCardRow(checked, disabled)}>
        {label}
        {o.trailing && (
          <span className={CHOICE_CARD_TRAILING}>{o.trailing}</span>
        )}
      </div>
    );
  if (o.trailing)
    return (
      <div className={CHOICE_ROW}>
        {label}
        <span className={CHOICE_TRAILING}>{o.trailing}</span>
      </div>
    );
  return label;
}
